import { useCallback } from "react";
import { FormattedMessage } from "react-intl";

import PrimaryButton from "@components/buttons/PrimaryButton";
import DateStamp from "@components/dates/DateStamp";
import styles from "@components/forms/careProfile/CareProfile.module.css";
import Tag from "@components/labels/Tag";
import BodyCopy from "@components/typography/BodyCopy";
import InternalLink from "@components/typography/InternalLink";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import { OpenerTypeEnum } from "@typing/Enums";
import {
  ConsultReportPublishDocument,
  ConsultReportPublishMutation,
  ConsultReportPublishMutationVariables,
  GetJourneyWithCareProfileQuery,
  InitialConsultReportPublishDocument,
  InitialConsultReportPublishMutation,
  InitialConsultReportPublishMutationVariables
} from "@typing/Generated";
import { sortByTimestamp } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";
import { consultStartsInFuture } from "@utils/timeUtils";

type Props = {
  journey: GetJourneyWithCareProfileQuery["journey"];
};

type Consult = NonNullable<GetJourneyWithCareProfileQuery["journey"]["consults"][number]>;

const ConsultReports = ({ journey }: Props) => {
  const myExpert = useMyExpert();

  const timeTrackingOpener = useGlobalOpener(OpenerTypeEnum.TIME_LOG);

  const sortedConsults = sortByTimestamp(
    journey.consults.filter(consult => !consult.canceled),
    consult => consult.consultReport?.publishedAt
  );

  const [initialConsultReportPublish] = useMutationWithValidation<
    InitialConsultReportPublishMutation,
    InitialConsultReportPublishMutationVariables
  >(InitialConsultReportPublishDocument, "initialConsultReport");

  const handlePublishInitialConsultReport = useCallback(
    (consultId: string) => {
      initialConsultReportPublish({ consultId, id: journey.initialConsultReport.id }).then(() => {
        timeTrackingOpener.open();
      });
    },
    [initialConsultReportPublish, journey.initialConsultReport.id, timeTrackingOpener]
  );

  const [consultReportPublish] = useMutationWithValidation<
    ConsultReportPublishMutation,
    ConsultReportPublishMutationVariables
  >(ConsultReportPublishDocument, "consultReport");

  const handlePublishConsultReport = useCallback(
    (consult: Consult) => {
      if (consult.consultReport?.id) {
        consultReportPublish({ id: consult.consultReport.id }).then(() => timeTrackingOpener.open());
      }
    },
    [consultReportPublish, timeTrackingOpener]
  );

  const hasPublishedReport = useCallback(
    (consult: Consult): boolean => {
      if (journey.initialConsultReport.consult?.id === consult.id) {
        return journey.initialConsultReport.published;
      } else {
        return consult.consultReport?.published ?? false;
      }
    },
    [journey.initialConsultReport.consult?.id, journey.initialConsultReport.published]
  );

  const finalizeSessionButton = useCallback(
    (consult: Consult) => {
      if (!hasPublishedReport(consult) && !consultStartsInFuture(consult)) {
        if (consult.position === 1) {
          return (
            <>
              <p>
                <BodyCopy>
                  <FormattedMessage id="models.consultReport.initialConsultReportRemoved" />
                </BodyCopy>
              </p>

              <PrimaryButton
                onClick={() => handlePublishInitialConsultReport(consult.id)}
                textKey="models.consultReport.finalizeInitialConsultReport"
              />
            </>
          );
        } else {
          return (
            <>
              <p>
                <BodyCopy>
                  <FormattedMessage id="models.consultReport.consultReportRemoved" />
                </BodyCopy>
              </p>

              <PrimaryButton
                onClick={() => handlePublishConsultReport(consult)}
                textKey="models.consultReport.finalizeConsultReport"
              />
            </>
          );
        }
      }
    },
    [handlePublishConsultReport, handlePublishInitialConsultReport, hasPublishedReport]
  );

  return (
    <>
      {sortedConsults.map(consult => (
        <div className={styles.consultReport} key={consult.id}>
          <div className={styles.spreadRow}>
            <BodyCopy color="gray-500" underline>
              {consult.position === 1 ? (
                <FormattedMessage
                  id="pages.carePlan.base.expert.initialConsultReport"
                  values={{
                    date: <DateStamp value={consult.startTime} />
                  }}
                />
              ) : (
                <FormattedMessage
                  id="pages.carePlan.base.expert.consultReport"
                  values={{ date: <DateStamp value={consult.startTime} /> }}
                />
              )}
            </BodyCopy>
            {consultStartsInFuture(consult) ? (
              <BodyCopy>
                <FormattedMessage id="models.consult.notStarted" />
              </BodyCopy>
            ) : (
              <>
                {hasPublishedReport(consult) ? (
                  <Tag color="success">
                    <FormattedMessage id="models.consult.finalized" />
                  </Tag>
                ) : (
                  <InternalLink
                    stylized={false}
                    to={journeyRoute({
                      journeyId: consult.journey.id,
                      linkAfterJourney: `/care-profile?docSection=sessions`,
                      myExpert
                    })}
                  >
                    <Tag color="danger">
                      <FormattedMessage id="models.consult.notFinalized" />
                    </Tag>
                  </InternalLink>
                )}
              </>
            )}
          </div>
          {finalizeSessionButton(consult)}
        </div>
      ))}
    </>
  );
};

export default ConsultReports;
